/**
 * API ↔ domain mappers.
 *
 * The backend speaks snake_case and keeps stock as separate rows; the frontend
 * domain model is camelCase with sizes embedded on the product. These mappers
 * are the single boundary where those two worlds meet, so the rest of the app
 * keeps using the existing domain types unchanged.
 */

import type {
  Order,
  OrderStatus,
  Product,
  ProductCategory,
  ProductCondition,
  Reservation,
  ReservationStatus,
  SizeStock,
} from "@/types/domain";
import type { Role, User } from "@/types/auth";

// ── Raw API shapes (what the backend actually returns) ──

export interface ApiUser {
  id: string;
  role: Role;
  business_name: string;
  phone: string;
  market: string;
  subscription_tier: string;
  subscription_expires_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApiStock {
  id: string;
  product_id: string;
  wholesaler_id: string;
  size: string;
  quantity: number;
  reserved: number;
  available: number;
  updated_at: string;
}

export interface ApiProduct {
  id: string;
  wholesaler_id: string;
  name: string;
  brand: string;
  sku: string;
  category: string;
  description: string;
  condition: string;
  image_urls: string[];
  price: number;
  is_visible: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  stock: ApiStock[] | null;
  // Denormalised names are not returned on products; the UI falls back to "".
  wholesaler_name?: string;
  wholesaler_market?: string;
}

export interface ApiOrder {
  id: string;
  wholesaler_id: string;
  reseller_id: string;
  product_id: string;
  reservation_id: string;
  size: string;
  quantity: number;
  unit_price: number;
  commission_rate: number;
  commission_amount: number;
  status: OrderStatus;
  product_name: string;
  brand_name: string;
  wholesaler_name: string;
  reseller_name: string;
  created_at: string;
  fulfilled_at: string | null;
}

export interface ApiReservation {
  id: string;
  reseller_id: string;
  stock_id: string;
  quantity: number;
  status: ReservationStatus;
  expires_at: string;
  created_at: string;
  // Hydrated fields the cart endpoint joins in:
  product_id?: string;
  product_name?: string;
  brand?: string;
  wholesaler_id?: string;
  wholesaler_name?: string;
  size?: string;
  unit_price?: number;
  commission_rate?: number;
}

// ── Deterministic placeholder accent, so a product without a seed colour still
// gets a stable thumbnail tint (keyed off its id). ──
const CHART_ACCENTS = [
  "oklch(0.58 0.11 185)",
  "oklch(0.62 0.14 255)",
  "oklch(0.70 0.15 75)",
  "oklch(0.62 0.16 330)",
  "oklch(0.64 0.14 145)",
];

function accentFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return CHART_ACCENTS[h % CHART_ACCENTS.length];
}

// ── Mappers ──

export function mapUser(u: ApiUser): User {
  return {
    id: u.id,
    name: u.business_name,
    phone: u.phone,
    role: u.role,
    businessName: u.business_name,
    plan: capitalise(u.subscription_tier),
    language: "en",
  };
}

export function mapStock(s: ApiStock): SizeStock {
  return {
    size: s.size,
    qty: s.quantity,
    stockId: s.id,
    reserved: s.reserved,
    available: s.available,
  };
}

const VALID_CATEGORIES = new Set<ProductCategory>(["sneakers", "official", "casual", "lifestyle"]);
const VALID_CONDITIONS = new Set<ProductCondition>(["new", "like_new", "good", "fair"]);

export function mapProduct(p: ApiProduct, wholesalerName = "", wholesalerMarket?: string): Product {
  const sizes = (p.stock ?? [])
    .map(mapStock)
    .sort((a, b) => a.size.localeCompare(b.size, undefined, { numeric: true }));
  const category: ProductCategory = VALID_CATEGORIES.has(p.category as ProductCategory)
    ? (p.category as ProductCategory)
    : "sneakers";
  const condition: ProductCondition = VALID_CONDITIONS.has(p.condition as ProductCondition)
    ? (p.condition as ProductCondition)
    : "new";
  return {
    id: p.id,
    wholesalerId: p.wholesaler_id,
    wholesalerName: p.wholesaler_name ?? wholesalerName,
    wholesalerMarket: p.wholesaler_market ?? wholesalerMarket,
    name: p.name,
    brand: p.brand,
    category,
    description: p.description ?? "",
    condition,
    imageColor: p.image_urls?.length ? "" : accentFor(p.id),
    imageUrls: p.image_urls ?? [],
    sizes,
    price: p.price,
    visible: p.is_visible,
    updatedAt: p.updated_at,
  };
}

export function mapOrder(o: ApiOrder): Order {
  return {
    id: o.id,
    reference: `SS-${o.id.slice(0, 6).toUpperCase()}`,
    wholesalerId: o.wholesaler_id,
    wholesalerName: o.wholesaler_name,
    resellerId: o.reseller_id,
    resellerName: o.reseller_name,
    productId: o.product_id,
    productName: o.product_name,
    brand: o.brand_name,
    size: o.size,
    qty: o.quantity,
    unitPrice: o.unit_price,
    commissionRate: o.commission_rate,
    status: o.status,
    createdAt: o.created_at,
  };
}

export function mapReservation(r: ApiReservation): Reservation {
  return {
    id: r.id,
    productId: r.product_id ?? "",
    productName: r.product_name ?? "",
    brand: r.brand ?? "",
    wholesalerId: r.wholesaler_id ?? "",
    wholesalerName: r.wholesaler_name ?? "",
    resellerId: r.reseller_id,
    resellerName: "You",
    size: r.size ?? "",
    qty: r.quantity,
    unitPrice: r.unit_price ?? 0,
    commissionRate: r.commission_rate ?? 0.03,
    status: r.status,
    createdAt: r.created_at,
    expiresAt: r.expires_at,
    stockId: r.stock_id,
  };
}

function capitalise(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
